import { useState } from "react";
import Popover from "@mui/material/Popover";
import { Icon } from "@iconify/react";
import { VStack } from "../../components/utils";
import { stringFormat } from "../../utils/string";

const NOTE_REGEX = /^\[([^|]+)\|([^|]+)\|([^\]]+)\]\s(.+)$/;

type NoteEntry =
  | {
      type: "structured";
      state: string;
      author: string;
      date: string;
      text: string;
    }
  | { type: "legacy"; text: string };

export function parseNoteHistory(note: string): NoteEntry[] {
  if (!note) return [];
  try {
    const parsed = JSON.parse(note);
    if (Array.isArray(parsed)) {
      return parsed.map((e: any) => ({
        type: "structured" as const,
        state: e.state ?? "",
        author: e.author ?? "",
        date: e.date ?? "",
        text: e.text ?? "",
      }));
    }
  } catch (_) {
    // not JSON — fall through to legacy parser
  }
  return note
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean)
    .map((line) => {
      const m = line.match(NOTE_REGEX);
      if (m) {
        return {
          type: "structured" as const,
          state: m[1].trim(),
          author: m[2].trim(),
          date: m[3].trim(),
          text: m[4].trim(),
        };
      }
      return { type: "legacy" as const, text: line };
    });
}

function NoteEntryCard({ entry }: { entry: NoteEntry }) {
  if (entry.type === "legacy") {
    return (
      <p className="text-xs text-secondaryText py-1 px-0.5">{entry.text}</p>
    );
  }
  return (
    <div className="border border-borderColor rounded-md px-2.5 py-2 bg-white">
      <div className="flex flex-wrap items-center gap-1.5 mb-1">
        {entry.state && (
          <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-sm bg-blue-50 text-blue-600">
            {stringFormat(entry.state)}
          </span>
        )}
        {(entry.author || entry.date) && (
          <span className="text-[11px] text-gray-light">
            {[entry.author, entry.date].filter(Boolean).join(" · ")}
          </span>
        )}
      </div>
      <p className="text-xs text-secondaryText">{entry.text}</p>
    </div>
  );
}

// Renders only the single most recent note entry inline (fixed height, no
// ambiguity about where one row's content ends and the next begins). The
// full chronological history is available on demand via a popover — cramming
// every past note into the grid row made it impossible to tell entries in one
// row apart from the row below.
export function BulkGrantNoteCell({ note }: { note: string }) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const entries = parseNoteHistory(note);

  if (entries.length === 0)
    return <span className="text-xs text-gray-light">—</span>;

  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setAnchorEl(e.currentTarget);
        }}
        className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:underline"
      >
        <Icon icon="ri:history-line" height={13} />
        View note{entries.length !== 1 ? "s" : ""} ({entries.length})
      </button>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <VStack className="gap-1.5 p-3 w-[320px] max-h-[400px] overflow-y-auto">
          <p className="text-xs font-semibold text-primaryText mb-1">
            Note history
          </p>
          {[...entries].reverse().map((entry, i) => (
            <NoteEntryCard key={i} entry={entry} />
          ))}
        </VStack>
      </Popover>
    </>
  );
}
